/**
 * WorkflowCase / WorkflowTemplate のタグ (Label + Color) に関する純関数・定数群。
 * バックエンドの `go/internal/backend/domain/valueobject/tag_color.go` (TagColor 8 値 enum) と
 * `workflow_case_tag.go` (label トリム + 空文字拒否 + max 20 文字 + 重複禁止) の値域・制約に
 * 追従する。ここでの検証は UX 目的の事前チェックにすぎず、業務判断の正は backend。
 */

/**
 * 8 色パレット (backend TagColor.String() の出力と同じ小文字値)。
 * design/demand/cases.html の `TAG_COLORS` 定数と同じ並び順。
 *
 * 表示色は `WORKFLOW_CASE_TAG_COLOR_VALUES` に定義する。 brand palette では
 * 赤↔ピンク / 青↔シアン / 緑↔ティール が近似して識別しにくかったため、
 * design/css/tokens.css の `--tag-*` トークン値を採用 (#4544)。
 */
export const WORKFLOW_CASE_TAG_COLORS = [
  'red',
  'blue',
  'green',
  'yellow',
  'purple',
  'teal',
  'cyan',
  'pink',
] as const

export type WorkflowCaseTagColorValue =
  (typeof WORKFLOW_CASE_TAG_COLORS)[number]

/** design HTML の tag-editor 既定色 (`var newTagColor = 'blue'`) と揃える。 */
export const WORKFLOW_CASE_TAG_DEFAULT_COLOR: WorkflowCaseTagColorValue = 'blue'

/**
 * タグ専用 8 色パレットの実色値。 design/css/tokens.css の `--tag-*` と 1:1。
 * brand palette 流用では識別が困難だったため、各 color 間で色相を約 45° 離している。
 */
export const WORKFLOW_CASE_TAG_COLOR_VALUES: Record<
  WorkflowCaseTagColorValue,
  string
> = {
  red: '#e0271f',
  yellow: '#d99a00',
  green: '#269e44',
  teal: '#12967e',
  cyan: '#1590e6',
  blue: '#2b2fd6',
  purple: '#8a3fd6',
  pink: '#d6318f',
}

/**
 * wire 由来の色文字列に対するタグ表示用 style を返す。
 * 未知の値 (想定外の DB drift 等) は undefined を返し、caller は gray フォールバックを使う。
 *
 * design/demand/cases.html の .case-tag と同一の color-mix 比率:
 * - color: tag 色 40% + text-high-emphasis 60% (淡色でも可読)
 * - background: tag 色 16% + surface-primary 84%
 * - border: transparent (枠線が「クリックできそう」に見えるため)
 */
export function getWorkflowCaseTagColorStyle(
  color: string,
  surfacePrimary: string,
  textHighEmphasis: string,
):
  | {
      color: string
      backgroundColor: string
      borderColor: 'transparent'
      borderWidth: '1px'
      borderStyle: 'solid'
    }
  | undefined {
  if (!isKnownWorkflowCaseTagColor(color)) return undefined
  const hex = WORKFLOW_CASE_TAG_COLOR_VALUES[color]
  return {
    color: `color-mix(in srgb, ${hex} 40%, ${textHighEmphasis})`,
    backgroundColor: `color-mix(in srgb, ${hex} 16%, ${surfacePrimary})`,
    borderColor: 'transparent',
    borderWidth: '1px',
    borderStyle: 'solid',
  }
}

/**
 * **押せる**タグチップの hover 背景。押せないチップには使わない。
 *
 * design `admin/case-templates.html:208`
 * (`button.ct-tag.ct-tag-suggest-btn:hover`) と `demand/cases.html:121`
 * (`button.case-tag.tag-suggest-btn:hover`) が
 * `color-mix(in srgb, var(--tc) 22%, var(--surface-primary))`。
 * 基準 (16%) と**同じタグ色を濃くする**だけで、色相は変えない。
 *
 * [DS 差異] DS の押せる面の hover は中立オーバーレイ `Hover.lowEmphasis`
 * (= `rgba(0,0,0,0.04)`) で、`MultiSelect` / `RichActionMenu` / `ToggleButton` 等は
 * これを使う。タグではそれを採らない:
 *
 * 1. タグ色は**ユーザーが付けた識別色**で、チップの意味そのもの。中立の黒を
 *    重ねると色相が濁り、識別色としての役割が落ちる
 * 2. 既に 16% で色が乗った面に 4% の黒を重ねても**ほぼ知覚できない**
 *    (押せることが伝わらず、hover を付けた意味が無くなる)
 *
 * **DS の思想 (= hover は押せる要素にだけ与える) はそのまま守る**。変えるのは
 * 色の作り方だけで、タグ色を持たないチップ (未知の色) には DS の
 * `Hover.lowEmphasis` を使う (呼び出し側の fallback)。
 *
 * なお DS `tag.recipe.ts` に `_hover` の定義は無い (2026-09-16 確認)。DS の Tag は
 * 表示専用の想定なので、**上書きではなく DS が定義していない状態の追加**にあたる。
 */
export function getWorkflowCaseTagHoverBackground(
  color: string,
  surfacePrimary: string,
): string | undefined {
  if (!isKnownWorkflowCaseTagColor(color)) return undefined
  const hex = WORKFLOW_CASE_TAG_COLOR_VALUES[color]
  return `color-mix(in srgb, ${hex} 22%, ${surfacePrimary})`
}

/** backend `NewWorkflowCaseTag` の label 制約 (1〜20 文字、トリム後)。 */
export const WORKFLOW_CASE_TAG_LABEL_MAX_LENGTH = 20

/** backend `WithUpdatedTags` / `validateWorkflowCaseTags` の上限 (AC-TAG-004)。 */
export const WORKFLOW_CASE_TAG_MAX_COUNT = 10

/**
 * 組織全体のタグプール総数上限 (AC-TAG-005/AC-TAG-006)。
 * backend `valueobject.WorkflowCaseTagPoolLimit` と同じ値。
 */
export const WORKFLOW_CASE_TAG_POOL_MAX_COUNT = 20

export function isKnownWorkflowCaseTagColor(
  color: string,
): color is WorkflowCaseTagColorValue {
  return (WORKFLOW_CASE_TAG_COLORS as readonly string[]).includes(color)
}

/**
 * wire 由来の色文字列を表示用の colorPalette に正規化する。
 * 未知の値 (想定外の DB drift 等) は gray にフォールバックし、表示崩れ・例外を防ぐ。
 */
export function workflowCaseTagColorPalette(color: string): string {
  return isKnownWorkflowCaseTagColor(color) ? color : 'gray'
}

/** AC-02: label の前後空白を自動トリムする (backend と同じ規則)。 */
export function normalizeWorkflowCaseTagLabel(raw: string): string {
  return raw.trim()
}

/** AC-03: 同一ワークフロー内の label 重複判定 (case-sensitive)。 */
export function isDuplicateWorkflowCaseTagLabel(
  tags: ReadonlyArray<{ label: string }>,
  label: string,
): boolean {
  return tags.some((tag) => tag.label === label)
}
